import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

// Custom hook for fetching data with loading and error states
export const useApiData = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiFunction();
        setData(result);
      } catch (err) {
        setError(err.message || 'An error occurred');
        console.error('API fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, dependencies);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction();
      setData(result);
    } catch (err) {
      setError(err.message || 'An error occurred');
      console.error('API refetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
};

// Hook for company stats
export const useCompanyStats = () => {
  return useApiData(apiService.getCompanyStats);
};

// Hook for testimonials
export const useTestimonials = (featuredOnly = false) => {
  return useApiData(() => apiService.getTestimonials(featuredOnly), [featuredOnly]);
};

// Hook for services
export const useServices = () => {
  return useApiData(apiService.getServices);
};

// Hook for form submissions
export const useFormSubmission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submitForm = async (apiFunction, formData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const result = await apiFunction(formData);
      setSuccess(true);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  return {
    loading,
    error,
    success,
    submitForm,
    resetState
  };
};

export default {
  useApiData,
  useCompanyStats,
  useTestimonials,
  useServices,
  useFormSubmission
};